import type { BookedAncillaryDTO, BookingDTO, LegDTO } from '@/types/dto/agent';

export const isBundledAncillary = (ancillary: BookedAncillaryDTO): boolean =>
  !!(
    ancillary.productTags?.includes('BundledProduct') ||
    ancillary.products?.some((product) =>
      product.productTags?.includes('BundledProduct')
    )
  );

export const isRefundableAncillary = (ancillary: BookedAncillaryDTO) =>
  ancillary.status !== 'REFUNDED' &&
  ancillary.refundable === 'YES' &&
  !isBundledAncillary(ancillary);

export const getAncillariesByLegId = (
  booking: BookingDTO | null
): Record<NonNullable<LegDTO['id']>, Array<BookedAncillaryDTO>> => {
  const ancillariesByLegId: Record<
    NonNullable<LegDTO['id']>,
    Array<BookedAncillaryDTO>
  > = {};

  booking?.bookedTrips?.forEach(({ bookedOffers }) =>
    bookedOffers?.forEach(({ admissions }) =>
      admissions?.forEach(({ coveredLegIds, ancillaries }) =>
        coveredLegIds?.forEach((id) => {
          ancillariesByLegId[id] = [
            ...(ancillariesByLegId[id] || []),
            ...(ancillaries || []),
          ];
        })
      )
    )
  );

  return ancillariesByLegId;
};

// For change travel class flow - create a key to match two ancillaries in exchange process by their products,
// that seem to be same ancillary, just carried from one offer to another one.
// Fallback to random string.
export const createAncillaryProductKey = (
  ancillary: BookedAncillaryDTO
): string => {
  const productKey =
    [
      (ancillary.products ?? [])
        .map(({ code, type }) => `${code}-${type}`)
        .join('-') ||
        `${ancillary.productDescription}-${ancillary.price?.amount}-${ancillary.price?.currency}`,
      (ancillary.passengerIds ?? []).join('-'),
    ].join('-') ||
    ancillary.id ||
    Math.random().toString();
  return productKey;
};

// If it's a ancillary going through change travel class flow,
// we want to hide duplicate ancillary row since there is going to be two of these -
// one with status `PREBOOKED` and the other one with status `EXCHANGE_ONGOING`.
export const isOngoingExchangeAncillary = (
  ancillary: BookedAncillaryDTO,
  admissionAncillaries?: Array<BookedAncillaryDTO>
): boolean => {
  if (
    ancillary.status !== 'EXCHANGED' &&
    ancillary.status !== 'EXCHANGE_ONGOING'
  ) {
    return false;
  }
  const productKey = createAncillaryProductKey(ancillary);
  const hasPrebookedAncillary = (admissionAncillaries ?? []).some(
    (ancillary) =>
      ancillary.status === 'PREBOOKED' &&
      productKey === createAncillaryProductKey(ancillary)
  );
  return hasPrebookedAncillary;
};
